import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileSpreadsheet, Search } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { useStoredState } from "@/hooks/useStoredState";
import { ordersApi } from "../api/orders";
import { useOrders } from "../hooks/useOrders";
import { useAcceptOrder, useRejectOrder, useAcknowledgeOrder } from "../hooks/useOrders";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { PageLoader } from "@/components/ui/Spinner";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { MobileSortBar, SortableTh, useTableSort } from "@/components/ui/TableSort";
import { naturalCompare } from "@/utils/naturalCompare";
import { formatDate } from "@/utils/formatDate";
import { ORDER_STATUS_LABELS, ORDER_TYPE_LABELS, PRIORITY_LABELS, type Order, type OrderStatus, type OrderType, type Priority } from "../types";
import { OrderTypeBadge } from "../components/OrderTypeBadge";

const SELECT_CLS =
  "text-xs border border-gray-200 dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]";

const statusColors: Record<OrderStatus, "gray" | "blue" | "green" | "red" | "yellow" | "purple" | "orange"> = {
  sent: "blue", accepted: "green", rejected: "red", in_progress: "orange",
  in_progress_partial: "yellow", submitted_for_acceptance: "purple",
  accepted_final: "green", closed: "gray", cancelled: "gray",
};

const ACTIVE_STATUSES: OrderStatus[] = [
  "sent", "accepted", "in_progress", "in_progress_partial", "submitted_for_acceptance",
];

type SortField = "order_number" | "localities" | "ordered_by" | "priority" | "status" | "approved_at" | "deadline";

const SORT_LABELS: Record<SortField, string> = {
  order_number: "Nr zamówienia",
  localities: "Miejscowość",
  ordered_by: "Zlecający",
  priority: "Priorytet",
  status: "Status",
  approved_at: "Data złożenia",
  deadline: "Termin",
};

// Braki (bez terminu / miejscowości / zlecającego) zawsze na końcu, niezależnie od kierunku.
const EMPTY_CHECK: Partial<Record<SortField, (o: Order) => boolean>> = {
  deadline: (o) => !o.deadline,
  localities: (o) => (o.localities ?? []).length === 0,
  ordered_by: (o) => !o.ordered_by_name,
};

// Kolejność statusów jak w domyślnym sortowaniu serwera (_ORDER_STATUS_ORDER w views.py):
// najpierw wymagające działania, na końcu zakończone / odrzucone / anulowane.
const STATUS_RANK: Record<OrderStatus, number> = {
  submitted_for_acceptance: 0, in_progress: 1, in_progress_partial: 2, accepted: 3,
  sent: 4, accepted_final: 5, closed: 6, rejected: 7, cancelled: 8,
};

const PRIORITY_RANK: Record<Priority, number> = { low: 0, standard: 1, high: 2 };

function compareOrders(a: Order, b: Order, field: SortField): number {
  switch (field) {
    case "order_number":
      return naturalCompare(a.order_number, b.order_number);
    case "localities":
      return (a.localities ?? []).join(", ").localeCompare((b.localities ?? []).join(", "), "pl");
    case "ordered_by":
      return (a.ordered_by_name ?? "").localeCompare(b.ordered_by_name ?? "", "pl");
    case "priority":
      return PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
    case "status":
      return STATUS_RANK[a.status] - STATUS_RANK[b.status];
    case "approved_at":
      return new Date(a.approved_at).getTime() - new Date(b.approved_at).getTime();
    case "deadline":
      return (a.deadline ?? "").localeCompare(b.deadline ?? "");
  }
}

function getDaysOverdue(deadline: string | null): number {
  if (!deadline) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(deadline);
  d.setHours(0, 0, 0, 0);
  return Math.max(Math.floor((today.getTime() - d.getTime()) / 86400000), 0);
}

export function ContractorOrdersPage() {
  const { data: orders, isLoading } = useOrders();
  const acceptMutation = useAcceptOrder();
  const rejectMutation = useRejectOrder();
  const acknowledgeMutation = useAcknowledgeOrder();

  // Client-side sort & filters: the contractor list is fetched whole (no pagination)
  // and `localities` only exists after serialization, so the server can't order by it.
  const { sortField, sortDir, handleSort, setField, toggleDir } = useTableSort<SortField>();
  // Filters survive opening an order's detail page and coming back (sessionStorage),
  // same as the GK orders list.
  const [search, setSearch] = useStoredState("contractor-orders:search", "");
  const [filterStatus, setFilterStatus] = useStoredState<OrderStatus | "">("contractor-orders:status", "");
  const [filterPriority, setFilterPriority] = useStoredState<Priority | "">("contractor-orders:priority", "");
  const [filterType, setFilterType] = useStoredState<OrderType | "">("contractor-orders:order-type", "");
  const [filterOrderedBy, setFilterOrderedBy] = useStoredState<string>("contractor-orders:ordered-by", "");
  const [filterDeadlineFrom, setFilterDeadlineFrom] = useStoredState<string>("contractor-orders:deadline-from", "");
  const [filterDeadlineTo, setFilterDeadlineTo] = useStoredState<string>("contractor-orders:deadline-to", "");
  const [filterApprovedFrom, setFilterApprovedFrom] = useStoredState<string>("contractor-orders:approved-from", "");
  const [filterApprovedTo, setFilterApprovedTo] = useStoredState<string>("contractor-orders:approved-to", "");

  const hasActiveFilters =
    !!search || !!filterStatus || !!filterPriority || !!filterType || !!filterOrderedBy ||
    !!filterDeadlineFrom || !!filterDeadlineTo || !!filterApprovedFrom || !!filterApprovedTo;

  // Coordinators who placed any of the loaded orders — the whole list is already
  // in memory, so the dropdown options come from it instead of a separate endpoint.
  const orderedByOptions = useMemo(() => {
    const names = new Set<string>();
    for (const o of orders ?? []) {
      if (o.ordered_by_name) names.add(o.ordered_by_name);
    }
    return [...names].sort((a, b) => a.localeCompare(b, "pl"));
  }, [orders]);

  // Only the types this contractor actually has, in the canonical order.
  const orderTypeOptions = useMemo(() => {
    const present = new Set((orders ?? []).map((o) => o.order_type));
    return (Object.keys(ORDER_TYPE_LABELS) as OrderType[]).filter((t) => present.has(t));
  }, [orders]);

  const sortedOrders = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = orders ?? [];
    if (q) {
      list = list.filter(
        (o) =>
          o.order_number.toLowerCase().includes(q) ||
          (o.localities ?? []).some((l) => l.toLowerCase().includes(q))
      );
    }
    if (filterStatus) list = list.filter((o) => o.status === filterStatus);
    if (filterPriority) list = list.filter((o) => o.priority === filterPriority);
    if (filterType) list = list.filter((o) => o.order_type === filterType);
    if (filterOrderedBy) list = list.filter((o) => o.ordered_by_name === filterOrderedBy);
    // Deadline range: ISO dates compare correctly as strings; orders without
    // a deadline can't fall inside a range, so they drop out.
    if (filterDeadlineFrom) list = list.filter((o) => !!o.deadline && o.deadline >= filterDeadlineFrom);
    if (filterDeadlineTo) list = list.filter((o) => !!o.deadline && o.deadline <= filterDeadlineTo);
    // approved_at is an ISO datetime — compare its date part against the picked dates.
    if (filterApprovedFrom) list = list.filter((o) => o.approved_at.slice(0, 10) >= filterApprovedFrom);
    if (filterApprovedTo) list = list.filter((o) => o.approved_at.slice(0, 10) <= filterApprovedTo);
    if (!sortField) return list;
    const dir = sortDir === "asc" ? 1 : -1;
    const isEmpty = EMPTY_CHECK[sortField];
    return [...list].sort((a, b) => {
      if (isEmpty) {
        const emptyA = isEmpty(a);
        const emptyB = isEmpty(b);
        if (emptyA !== emptyB) return emptyA ? 1 : -1;
      }
      return dir * compareOrders(a, b, sortField);
    });
  }, [orders, search, filterStatus, filterPriority, filterType, filterOrderedBy, filterDeadlineFrom, filterDeadlineTo, filterApprovedFrom, filterApprovedTo, sortField, sortDir]);

  // Eksport działek odfiltrowanych zamówień — ID-ki widocznych wierszy idą do
  // backendu, plik wraca blobem (autoryzowany endpoint) i zapisuje się od razu.
  const [exportError, setExportError] = useState<string | null>(null);
  const exportMutation = useMutation({
    mutationFn: (orderIds: string[]) => ordersApi.downloadParcelsXlsx(orderIds),
    onError: async (e: unknown) => {
      // The blob responseType wraps error JSON in a Blob — unwrap it for the real message.
      const data = (e as { response?: { data?: unknown } })?.response?.data;
      let message = "Eksport nieudany — spróbuj ponownie.";
      if (data instanceof Blob) {
        try {
          message = (JSON.parse(await data.text()) as { errors?: string[] }).errors?.[0] ?? message;
        } catch {
          // keep the generic message
        }
      }
      setExportError(message);
    },
  });

  function startParcelsExport() {
    setExportError(null);
    exportMutation.mutate(sortedOrders.map((o) => o.id));
  }

  // Rejection flow: track which order is being rejected + comment text
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectComment, setRejectComment] = useState("");
  const [rejectError, setRejectError] = useState("");

  function startReject(id: string) {
    setRejectingId(id);
    setRejectComment("");
    setRejectError("");
  }

  function confirmReject() {
    if (!rejectingId) return;
    if (!rejectComment.trim()) {
      setRejectError("Komentarz jest wymagany.");
      return;
    }
    rejectMutation.mutate(
      { id: rejectingId, comment: rejectComment.trim() },
      {
        onSuccess: () => { setRejectingId(null); setRejectComment(""); setRejectError(""); },
        onError: () => setRejectError("Błąd zapisu. Spróbuj ponownie."),
      }
    );
  }

  return (
    <div>
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Moje zamówienia</h1>
        <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-6" />
      </div>
      {isLoading ? (
        <PageLoader />
      ) : (orders ?? []).length === 0 ? (
        <div className="bg-white dark:bg-gray-800 p-8 text-center text-gray-400 dark:text-gray-500 text-sm">
          Brak zamówień
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <div className="relative">
              <Search
                size={14}
                className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none"
              />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Szukaj: nr zamówienia, miejscowość…"
                className={`${SELECT_CLS} pl-7 w-64`}
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as OrderStatus | "")}
              className={SELECT_CLS}
            >
              <option value="">Wszystkie statusy</option>
              {(Object.entries(ORDER_STATUS_LABELS) as [OrderStatus, string][]).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value as Priority | "")}
              className={SELECT_CLS}
            >
              <option value="">Wszystkie priorytety</option>
              {(Object.entries(PRIORITY_LABELS) as [Priority, string][]).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
            {/* Także gdy wybrany typ zniknął z listy — inaczej nie dałoby się go odznaczyć. */}
            {(orderTypeOptions.length > 1 || !!filterType) && (
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as OrderType | "")}
                className={SELECT_CLS}
              >
                <option value="">Wszystkie typy</option>
                {orderTypeOptions.map((t) => (
                  <option key={t} value={t}>{ORDER_TYPE_LABELS[t]}</option>
                ))}
                {filterType && !orderTypeOptions.includes(filterType) && (
                  <option value={filterType}>{ORDER_TYPE_LABELS[filterType] ?? filterType}</option>
                )}
              </select>
            )}
            <SearchableSelect
              placeholder="Wszyscy zlecający"
              value={filterOrderedBy}
              onChange={(e) => setFilterOrderedBy(e.target.value)}
              options={orderedByOptions.map((name) => ({ value: name, label: name }))}
            />
            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              <span>Termin od</span>
              <DatePicker
                value={filterDeadlineFrom}
                onChange={setFilterDeadlineFrom}
                placeholder="dd.mm.rrrr"
                aria-label="Termin od"
                className="!py-1 w-[9.5rem] text-xs"
              />
              <span>do</span>
              <DatePicker
                value={filterDeadlineTo}
                onChange={setFilterDeadlineTo}
                placeholder="dd.mm.rrrr"
                aria-label="Termin do"
                className="!py-1 w-[9.5rem] text-xs"
              />
            </div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              <span>Złożone od</span>
              <DatePicker
                value={filterApprovedFrom}
                onChange={setFilterApprovedFrom}
                placeholder="dd.mm.rrrr"
                aria-label="Złożone od"
                className="!py-1 w-[9.5rem] text-xs"
              />
              <span>do</span>
              <DatePicker
                value={filterApprovedTo}
                onChange={setFilterApprovedTo}
                placeholder="dd.mm.rrrr"
                aria-label="Złożone do"
                className="!py-1 w-[9.5rem] text-xs"
              />
            </div>
            {hasActiveFilters && (
              <button
                onClick={() => {
                  setSearch("");
                  setFilterStatus("");
                  setFilterPriority("");
                  setFilterType("");
                  setFilterOrderedBy("");
                  setFilterDeadlineFrom("");
                  setFilterDeadlineTo("");
                  setFilterApprovedFrom("");
                  setFilterApprovedTo("");
                }}
                className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 underline"
              >
                Wyczyść filtry
              </button>
            )}
            {/* Eksport dokładnie tego, co widać po filtrach. */}
            <div className="ml-auto self-center">
              <Button
                size="sm"
                variant="ghost"
                onClick={startParcelsExport}
                disabled={exportMutation.isPending || sortedOrders.length === 0}
                loading={exportMutation.isPending}
                title="Pobierz działki odfiltrowanych zamówień jako arkusz Excel — wyłącznie właściciele przekazani w Twoich zamówieniach (identyfikator działki, adres, właściciel, nr zamówienia)"
              >
                <FileSpreadsheet size={13} /> Pobierz działki (.xlsx)
              </Button>
            </div>
          </div>
          {exportError && (
            <p className="mb-3 text-[11px] font-mono text-[var(--color-primary)]">{exportError}</p>
          )}
          {/* Mobile sort bar — the table headers do this job on desktop */}
          <MobileSortBar
            id="contractor-orders-sort"
            labels={SORT_LABELS}
            sortField={sortField}
            sortDir={sortDir}
            onFieldChange={setField}
            onDirToggle={toggleDir}
          />
          {/* Mobile card list */}
          <div className="sm:hidden flex flex-col divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {sortedOrders.map((order) => {
              const daysOverdue = ACTIVE_STATUSES.includes(order.status) ? getDaysOverdue(order.deadline) : 0;
              const needsAck = order.status === "accepted" && !order.contractor_acknowledged;
              return (
                <div
                  key={order.id}
                  className={`px-4 py-3 ${daysOverdue > 0 ? "bg-red-50 dark:bg-red-900/10" : needsAck ? "bg-blue-50 dark:bg-blue-900/10" : ""}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Link to={`/contractor/orders/${order.id}`} className="font-medium text-[var(--color-primary)] hover:underline">
                          {order.order_number}
                        </Link>
                        <OrderTypeBadge type={order.order_type} display={order.order_type_display} hideStandard />
                        {needsAck && (
                          <span className="text-xs bg-[var(--color-primary-dim)] text-[var(--color-primary)] rounded px-1.5 py-0.5 font-medium">Nowe</span>
                        )}
                        {order.pending_change_requests_count > 0 && (
                          <span
                            className="text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 rounded px-1.5 py-0.5 font-medium"
                            title="GK ELEKTIM prosi o akceptację zmiany zamówienia"
                          >
                            Zmiana do akceptacji ({order.pending_change_requests_count})
                          </span>
                        )}
                        {order.has_new_activity && (
                          <span
                            className="text-xs bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 rounded px-1.5 py-0.5 font-medium"
                            title={order.last_activity_label || "Nowa aktywność"}
                          >
                            {order.last_activity_label || "Nowa aktywność"}
                          </span>
                        )}
                      </div>
                      {/* Przedsięwzięcie jest celowo ukryte przed podwykonawcą — ale nie
                          miejscowość: bez niej geodeta nie wie, gdzie ma jechać. */}
                      {(order.localities ?? []).length > 0 && (
                        <p className="text-xs mt-0.5 text-gray-600 dark:text-gray-300">
                          {order.localities.join(", ")}
                        </p>
                      )}
                      {order.ordered_by_name && (
                        <p className="text-xs mt-0.5 text-gray-500 dark:text-gray-400">
                          Zlecający: {order.ordered_by_name}
                        </p>
                      )}
                      <p className="text-xs mt-0.5 text-gray-400 dark:text-gray-500">
                        Złożone: {formatDate(order.approved_at)}
                      </p>
                      {order.deadline && (
                        <p className={`text-xs mt-0.5 ${daysOverdue > 0 ? "text-red-600 font-semibold" : "text-gray-400 dark:text-gray-500"}`}>
                          Termin: {formatDate(order.deadline)}
                          {daysOverdue > 0 && ` · Opóźnione ${daysOverdue} ${daysOverdue === 1 ? "dzień" : "dni"}`}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <Badge color={order.priority === "high" ? "red" : "blue"}>{PRIORITY_LABELS[order.priority]}</Badge>
                      <Badge color={statusColors[order.status]}>{ORDER_STATUS_LABELS[order.status]}</Badge>
                    </div>
                  </div>
                  {order.status === "sent" && rejectingId !== order.id && (
                    <div className="flex gap-2 flex-wrap">
                      <Button size="sm" loading={acceptMutation.isPending} onClick={() => acceptMutation.mutate(order.id)}>Przyjmij</Button>
                      <Button size="sm" variant="danger" onClick={() => startReject(order.id)}>Odrzuć</Button>
                    </div>
                  )}
                  {order.status === "sent" && rejectingId === order.id && (
                    <div className="flex flex-col gap-2">
                      <textarea
                        className={`block w-full rounded-md border px-2 py-1.5 text-xs ${rejectError ? "border-red-400" : "border-gray-300"}`}
                        rows={2}
                        placeholder="Podaj powód odrzucenia..."
                        value={rejectComment}
                        onChange={(e) => { setRejectComment(e.target.value); setRejectError(""); }}
                      />
                      {rejectError && <p className="text-xs text-red-600">{rejectError}</p>}
                      <div className="flex gap-1.5 flex-wrap">
                        <Button size="sm" variant="danger" loading={rejectMutation.isPending} onClick={confirmReject}>Potwierdź odrzucenie</Button>
                        <Button size="sm" variant="secondary" onClick={() => setRejectingId(null)}>Anuluj</Button>
                      </div>
                    </div>
                  )}
                  {needsAck && (
                    <Button size="sm" variant="secondary" loading={acknowledgeMutation.isPending} onClick={() => acknowledgeMutation.mutate(order.id)}>
                      Oznacz jako przeczytane
                    </Button>
                  )}
                </div>
              );
            })}
            {sortedOrders.length === 0 && (
              <p className="px-4 py-8 text-center text-gray-400 dark:text-gray-500 text-sm">
                Brak zamówień pasujących do filtrów
              </p>
            )}
          </div>
          {/* Desktop table */}
          <div className="hidden sm:block bg-white dark:bg-gray-800 overflow-x-auto">
            <table className="w-full text-xs min-w-[840px]">
              <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <SortableTh field="order_number" label="Nr zamówienia" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableTh field="localities" label="Miejscowość" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableTh field="ordered_by" label="Zlecający" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableTh field="priority" label="Priorytet" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableTh field="status" label="Status" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableTh field="approved_at" label="Złożone" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableTh field="deadline" label="Termin" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Akcje</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedOrders.map((order) => {
                  const daysOverdue = ACTIVE_STATUSES.includes(order.status) ? getDaysOverdue(order.deadline) : 0;
                  const needsAck = order.status === "accepted" && !order.contractor_acknowledged;
                  return (
                    <tr
                      key={order.id}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 border-l-2 border-transparent hover:border-[var(--color-primary)] transition-colors ${daysOverdue > 0 ? "bg-red-50" : needsAck ? "bg-blue-50 dark:bg-blue-900/10" : ""}`}
                    >
                      <td className="px-4 py-3">
                        <Link to={`/contractor/orders/${order.id}`} className="font-medium text-[var(--color-primary)] hover:underline">
                          {order.order_number}
                        </Link>
                        <OrderTypeBadge type={order.order_type} display={order.order_type_display} hideStandard className="ml-2" />
                        {needsAck && (
                          <span className="ml-2 text-xs bg-[var(--color-primary-dim)] text-[var(--color-primary)] rounded px-1.5 py-0.5 font-medium">Nowe</span>
                        )}
                        {order.pending_change_requests_count > 0 && (
                          <span
                            className="ml-2 text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 rounded px-1.5 py-0.5 font-medium"
                            title="GK ELEKTIM prosi o akceptację zmiany zamówienia"
                          >
                            Zmiana do akceptacji ({order.pending_change_requests_count})
                          </span>
                        )}
                        {order.has_new_activity && (
                          <span
                            className="ml-2 text-xs bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 rounded px-1.5 py-0.5 font-medium"
                            title={order.last_activity_label || "Nowa aktywność"}
                          >
                            {order.last_activity_label || "Nowa aktywność"}
                          </span>
                        )}
                      </td>
                      {/* Gdzie jest robota — z działek zamówienia, a gdy ich nie ma,
                          z działek przedsięwzięcia. Sama nazwa przedsięwzięcia zostaje ukryta. */}
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                        {(order.localities ?? []).length > 0
                          ? order.localities.join(", ")
                          : <span className="text-gray-300 dark:text-gray-600">—</span>}
                      </td>
                      {/* Osoba zlecająca (koordynator) — kontakt jest w szczegółach zamówienia. */}
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                        {order.ordered_by_name ?? <span className="text-gray-300 dark:text-gray-600">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <Badge color={order.priority === "high" ? "red" : "blue"}>{PRIORITY_LABELS[order.priority]}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge color={statusColors[order.status]}>{ORDER_STATUS_LABELS[order.status]}</Badge>
                      </td>
                      {/* Data złożenia zamówienia — podwykonawca musi wiedzieć, kiedy zlecenie wpadło. */}
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500 dark:text-gray-400">
                        {formatDate(order.approved_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-0.5">
                          <span className={daysOverdue > 0 ? "text-red-600 font-medium" : "text-gray-500 dark:text-gray-400"}>
                            {formatDate(order.deadline)}
                          </span>
                          {daysOverdue > 0 && (
                            <span className="text-xs font-semibold text-red-600">
                              Opóźnione {daysOverdue} {daysOverdue === 1 ? "dzień" : "dni"}
                              {" — szczegóły kary w zamówieniu"}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {order.status === "sent" && rejectingId !== order.id && (
                          <div className="flex items-center gap-2">
                            <Button size="sm" loading={acceptMutation.isPending} onClick={() => acceptMutation.mutate(order.id)}>Przyjmij</Button>
                            <Button size="sm" variant="danger" onClick={() => startReject(order.id)}>Odrzuć</Button>
                          </div>
                        )}
                        {order.status === "sent" && rejectingId === order.id && (
                          <div className="flex flex-col gap-2 max-w-xs">
                            <textarea
                              className={`block w-full rounded-md border px-2 py-1.5 text-xs ${rejectError ? "border-red-400" : "border-gray-300"}`}
                              rows={2}
                              placeholder="Podaj powód odrzucenia..."
                              value={rejectComment}
                              onChange={(e) => { setRejectComment(e.target.value); setRejectError(""); }}
                            />
                            {rejectError && <p className="text-xs text-red-600">{rejectError}</p>}
                            <div className="flex gap-1.5">
                              <Button size="sm" variant="danger" loading={rejectMutation.isPending} onClick={confirmReject}>Potwierdź odrzucenie</Button>
                              <Button size="sm" variant="secondary" onClick={() => setRejectingId(null)}>Anuluj</Button>
                            </div>
                          </div>
                        )}
                        {needsAck && (
                          <Button size="sm" variant="secondary" loading={acknowledgeMutation.isPending} onClick={() => acknowledgeMutation.mutate(order.id)}>
                            Oznacz jako przeczytane
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {sortedOrders.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-400 dark:text-gray-500">
                      Brak zamówień pasujących do filtrów
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
